import { Button, Content, ContentVariants, Popover } from "@patternfly/react-core";
import { FormContainer } from "@rhc-shared-components/form";
import { FormCheckbox } from "@rhc-shared-components/form-checkbox";
import { FormRadio } from "@rhc-shared-components/form-radio-select";
import { FormSelect } from "@rhc-shared-components/form-select-component";
import { Formik } from "formik";
import { type FC, forwardRef } from "react";
import { ProjectContainerTypes, ProjectTypes } from "../../../../constants/project";
import { useCreateModalStore } from "../CreateModalStore";
import {
	CreateFormKeys,
	OperatorCreateInitialValues,
	OperatorCreateValidator,
	OperatorDistributionOptions,
	ProjectContainerKeys,
	ProjectCreateModalKeys,
	ProjectDistributionMethodName,
	SpecializedCertificationOptions,
	useCreateAndAttachComponent,
} from "../createConstants";
import "./CreateForms.scss";
import { InfoCircleIcon } from "@patternfly/react-icons";
import { useProductMetadata } from "../../../../hooks/useProductMetadata";
import {
	DeployedAsSetType,
	getAttachedProjectBadgeSet,
	getDeployedAsSet,
} from "../../../../utils/productDeployedAsSet";

interface OperatorsCreateFormProps {
	handleCreateModal: (showModal: boolean) => void;
	ref: any;
}
export const OperatorsCreateForm: FC<OperatorsCreateFormProps> = forwardRef((props, ref: any) => {
	const { createNewComponent } = useCreateAndAttachComponent(props.handleCreateModal, "new");
	const { modalData } = useCreateModalStore();
	const getDocumentationLink = (event: any) => {
		if (event.target.innerText === "Container Storage Interface (CSI)") {
			return window.open(
				"https://access.redhat.com/documentation/en-us/red_hat_software_certification/8.45/html/red_hat_software_certification_quick_start_guide/assembly_red-hat-openshift-certification_quick-start-guide#con_red-hat-container-storage-interface-certification_quick-start-guide-red-hat-openshift-certification",
			);
		} else if (event.target.innerText === "Container Network Interface (CNI)") {
			return window.open(
				"https://docs.redhat.com/en/documentation/red_hat_software_certification/8.45/html/red_hat_software_certification_quick_start_guide/assembly_red-hat-openshift-certification_quick-start-guide#con_red-hat-container-network-interface-certification_quick-start-guide-red-hat-openshift-certification",
			);
		}
	};

	// OpenShift Virtualization filtering: https://issues.redhat.com/browse/CONNCERT-2398
	// User cannot view or select 'OpenShift Virtualization' from the 'Add Component' modal in the following cases:
	// - If another OpenShift Virtualization component is already attached to the product listing
	// this filter is a counter filter to the above statement.
	// - user cant create a cni/csi if the user already have a OpenShift Virtualization project
	const { certificationListState } = useProductMetadata();
	const attachComponentTypes = getDeployedAsSet(certificationListState || []);
	const hideCNICSI =
		attachComponentTypes.has(DeployedAsSetType.openShiftVirtualization) ||
		attachComponentTypes.has(DeployedAsSetType.openShiftCnf); // https://issues.redhat.com/browse/CONNCERT-2448

	// Additional badge check: https://issues.redhat.com/browse/CONNCERT-2448
	const allBadges = getAttachedProjectBadgeSet(certificationListState || []);
	let specializedCertificationOptions = [...SpecializedCertificationOptions];
	if (allBadges.has("cni")) {
		specializedCertificationOptions = specializedCertificationOptions.filter((item) => item.value === "cni");
	}
	if (allBadges.has("csi")) {
		specializedCertificationOptions = specializedCertificationOptions.filter((item) => item.value === "csi");
	}

	const getInitialValues = () => {
		const operatorCreateInitialValues = { ...OperatorCreateInitialValues };
		operatorCreateInitialValues[CreateFormKeys.badges] = allBadges.has("csi") ? "csi" : "cni";
		return operatorCreateInitialValues;
	};
	return (
		<Formik
			innerRef={ref}
			initialValues={getInitialValues()}
			validationSchema={OperatorCreateValidator}
			onSubmit={async (values) => {
				const operatorDistribution =
					OperatorDistributionOptions?.find((opDist) => opDist.value === values?.[CreateFormKeys.operatorDistribution])
						?.key || "";
				const requestData = {
					[ProjectCreateModalKeys.name]: modalData.componentName,
					[ProjectCreateModalKeys.container]: {
						[ProjectContainerKeys.distributionMethod]: ProjectDistributionMethodName.redHatContainerRegistry,
						[ProjectContainerKeys.osContentType]: "Operator Bundle Image",
						[ProjectContainerKeys.type]: ProjectContainerTypes.operator,
					},
					[ProjectCreateModalKeys.marketplace]: {
						enablement_status: "ineligible",
					},
					[ProjectCreateModalKeys.operatorDistribution]: operatorDistribution,
					[ProjectCreateModalKeys.projectStatus]: "active",
					[ProjectCreateModalKeys.type]: ProjectTypes.containers,
					...(values[CreateFormKeys.isSpecializedCert] && {
						[ProjectCreateModalKeys.badges]: [values[CreateFormKeys.badges]],
					}),
				};
				await createNewComponent(requestData);
			}}
		>
			{({ values }) => (
				<FormContainer gridSpan={12}>
					<Content component={ContentVariants.small} style={{ color: "#777", marginBottom: "-16px" }}>
						<InfoCircleIcon color="#2b9af3" />
						&ensp; The following values can't be changed after the project has been created.
					</Content>
					{!hideCNICSI && (
						<>
							<FormCheckbox
								name={CreateFormKeys.isSpecializedCert}
								id={"specialized-certification"}
								label="Specialized Certification"
								subLabel={
									<span>
										My operator is a{" "}
										<span
											style={{ borderBottom: "dashed 1px #aaa" }}
											id={"popover-selector"}
											onClick={(e) => {
												e.preventDefault();
											}}
										>
											CNI or CSI
										</span>{" "}
										(this is uncommon)
										<Popover
											className={"component-popover"}
											triggerRef={() => document.getElementById("popover-selector") as HTMLButtonElement}
											bodyContent={
												<div>
													<Button variant="link" onClick={(event) => getDocumentationLink(event)} isInline>
														Container Storage Interface (CSI)
													</Button>{" "}
													are specialized drivers for OpenShift network and storage infrastructure.
													<Button variant="link" onClick={(event) => getDocumentationLink(event)} isInline>
														Container Network Interface (CNI)
													</Button>{" "}
													are specialized applications on OpenShift for Telecommunications.
												</div>
											}
										/>
									</span>
								}
							/>
							{values?.[CreateFormKeys.isSpecializedCert] && (
								<div style={{ paddingLeft: "1rem" }}>
									<FormRadio
										name={CreateFormKeys.badges}
										label=""
										isRequired
										options={specializedCertificationOptions}
										span={12}
									/>
								</div>
							)}
						</>
					)}
					<FormSelect
						label="Publication Options"
						subLabel={
							<Content>
								<Content component="p" className={"sub-label"}>
									Select the OpenShift OperatorHub catalog where this operator will be published.
								</Content>
							</Content>
						}
						name={CreateFormKeys.operatorDistribution}
						selectOptions={OperatorDistributionOptions}
						isRequired
						placeholder="Select publication option"
						isFullWidth
					/>
				</FormContainer>
			)}
		</Formik>
	);
});
